import type { Currency } from 'src/types/listing';
import type { EscrowVertical } from 'src/types/chain';
import type { EscrowOffer } from 'src/utils/escrowOffer';
import type { PurchaseGate } from 'src/utils/purchaseGate';

import { resolveEscrowOffer } from 'src/utils/escrowOffer';

import { useBuyerChains } from 'src/queries';

import { useUserSession } from './useUserSession';

interface Params {
  vertical: EscrowVertical;
  /** The listing's currency; `undefined` while the listing is still loading. */
  listingCurrency: Currency | undefined;
  purchaseGate: PurchaseGate;
}

/**
 * The escrow offer of a listing screen (app#176): `resolveEscrowOffer` fed from the registry and
 * the buyer's linked wallets, shared by the sale and the rental listing so the two read the same
 * facts the same way.
 *
 * The registry is asked only for a buyer past the gate — an anonymous visitor makes no request,
 * as batch 7 left it. The families are the wallets of the signed-in account (`/user/auth/me`):
 * the deal is paid by one of them, so they are the ones that can pay. Not known until the api has
 * answered — the offer then stays as it was, and the server has the last word.
 */
export const useEscrowOffer = ({ vertical, listingCurrency, purchaseGate }: Params): EscrowOffer => {
  const { account } = useUserSession();
  const { data: buyerChains } = useBuyerChains(purchaseGate === 'buy');

  if (!listingCurrency) {
    return { kind: 'offered' };
  }

  return resolveEscrowOffer({
    buyerChains,
    vertical,
    listingCurrency,
    linkedFamilies: account?.wallets?.map((wallet) => wallet.chainType),
  });
};
